import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { isNativeApp } from '../utils/deviceIdentity';
import { handleFormBack } from '../utils/formStack';
import { backTarget, mainPath } from '../utils/backStep';
import { endSessionLog } from '../utils/session';
import { useAuth } from '../contexts/AuthContext';

// The Android hardware back button (APK only). One press = ONE step back, the
// same ladder the keyboard climbs in BackKeyHandler — both ask utils/backStep:
//   1 anything OPEN (a form, a sheet, the menu drawer) closes, the page stays;
//   2 a page we walked into inside the app → one history step back;
//   3 a sub-page opened cold → its parent page;
//   4 anywhere else → the MAIN page (Dashboard for an admin, Guests for a
//     member) - it never walks back into the sign-in screens behind the app;
//   5 on the main page → "Do you want to exit?", and back dismisses that too.
//
// Reach the Capacitor App plugin through the global plugin registry (same
// no-bundler-import pattern as Device in utils/deviceIdentity.js), so the plain
// web build is completely unaffected.
function getCapApp() {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.App || null;
}

export default function BackButtonHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const { user, isAdmin, can } = useAuth();
  const [askExit, setAskExit] = useState(false);

  // The listener is registered once, so everything it reads lives in a ref.
  const locRef = useRef(location);
  locRef.current = location;
  const askExitRef = useRef(askExit);
  askExitRef.current = askExit;
  // The main page of whoever is signed in (ONE rule, utils/backStep).
  const homeRef = useRef('/');
  homeRef.current = mainPath({ signedIn: !!user, isAdmin, canGuests: can('CanMembers') });
  // How many in-app steps are left to pop. Sign-in / sign-out navigate with
  // replace, so they never count; a POP (the button itself) unwinds one.
  const depth = useRef(0);
  useEffect(() => {
    if (navType === 'PUSH') depth.current += 1;
    else if (navType === 'POP') depth.current = Math.max(0, depth.current - 1);
  }, [location.key, navType]);

  const exitApp = useCallback(() => {
    setAskExit(false);
    endSessionLog(); // stamp the final logout time before quitting (best-effort)
    const App = getCapApp();
    if (App?.exitApp) App.exitApp();
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const App = getCapApp();
    if (!App?.addListener) return undefined;

    let handle;
    const sub = App.addListener('backButton', () => {
      // The exit prompt itself: back dismisses it (the same as "No").
      if (askExitRef.current) { setAskExit(false); return; }
      // Anything open closes first, and the page stays where it is.
      if (handleFormBack()) return;
      const go = backTarget({
        path: locRef.current.pathname,
        depth: depth.current,
        main: homeRef.current,
        canExit: true,
      });
      if (go === null) return;
      if (go === 'exit') { setAskExit(true); return; }
      setAskExit(false);
      if (go === -1) navigate(-1);
      else navigate(go, { replace: true });   // '/app' lands on the main page
    });
    // addListener may return a handle or a Promise<handle> depending on version.
    Promise.resolve(sub).then((h) => { handle = h; });

    return () => { if (handle && handle.remove) handle.remove(); };
  }, [navigate]);

  if (!askExit) return null;

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true">
      <div style={styles.box}>
        <p style={styles.msg}>Do you want to exit?</p>
        <div style={styles.actions}>
          <button type="button" style={styles.btnGhost} onClick={() => setAskExit(false)}>No</button>
          <button type="button" style={styles.btnPrimary} onClick={exitApp}>Yes</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  },
  box: {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 320,
    padding: '22px 20px 16px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
    textAlign: 'center',
  },
  msg: { margin: '0 0 18px', fontSize: 16, fontWeight: 600, color: '#1f2937' },
  actions: { display: 'flex', gap: 10, justifyContent: 'center' },
  btnGhost: {
    flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #d1d5db',
    background: '#fff', color: '#374151', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  btnPrimary: {
    flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
    background: '#dc2626', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
};
